import {
    Component,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    isDevMode,
    OnInit,
} from '@angular/core';
import { format } from 'date-fns';
import { ECharts, EChartsOption } from 'echarts';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { BehaviorSubject, debounceTime, NEVER, switchMap, tap } from 'rxjs';
import { XYChartDashoardItemFactory } from 'src/app/dashboards/common/items/xy-chart';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import {
    DataLoaderResult,
    IDataLoaderResult,
    SQL_NOT_AVAILABLE,
} from 'src/app/services/data-loader/data-loader-result';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { IDashboardContext } from 'src/app/services/query-context/models/dashboard-context.model';
import { IQueryContext } from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import {
    IDashboardItem,
    IDashboardItemFactory,
    IDashboardItemFactoryOptions,
} from 'src/app/utils/dashboard-item';
import { StackingMode } from 'src/app/utils/echarts/apply-stacking-mode';
import { changeDatasetSourceFormat } from 'src/app/utils/echarts/change-dataset-format';
import {
    dataExporterCreator,
    echartsExportDataSplitGroup,
} from 'src/app/utils/echarts/data-export';
import {
    changeSeriesType,
    getSeriesType,
} from 'src/app/utils/echarts/toggle-series-type';
import { JSONPrettyStringify } from 'src/app/utils/json';
import { IEChartsDashboardItemPersistentOptions } from './echarts-dashboard-item-persistent-options.model';

export type EChartsDashboardItemHeight = '200' | '400' | '600' | '800';

export interface IEChartsDashboardItem
    extends Omit<IDashboardItem, 'composeData'> {
    dashboardName: string;
    /** Chart title */
    title: string | ((context: IQueryContext) => string);
    /** Chart subtitle */
    subtitle?: string;
    /** Initial options used by parent Factory */
    initFactoryOptions?: any;
    /** A function that composes default EChartsOption from DataloaderResult
     * Created EChartsOption can be later modified by user actions
     * @see EChartsOption
     * @see DataLoaderResult
     */
    composeData: (
        rawData: any,
        context?: IQueryContext,
        echartsInstance?: ECharts,
        options?: any
    ) => EChartsOption;
    /** A group of utility tools. */
    toolbox?: {
        /** The configuration item for each tool. */
        feature?: {
            /** Export data to csv*/
            exportDataToCSV?: {
                /** Name of exported file. */
                name?: string;
                /** A function that transforms EChartsOption to a 2D array. */
                dataExporter?: (echartsOptions: any) => void;
            };
            /** Export data to xlsx*/
            exportDataToXLSX?: {
                /** Name of exported file. */
                name?: string;
                /** A function that transforms EChartsOption to a 2D array. */
                dataExporter?: (echartsOptions: any) => void;
            };
            /** Export data to clipboard*/
            exportDataToClipboard?: {
                /** A function that transforms EChartsOption to a 2D array. */
                dataExporter?: (echartsOptions: any) => void;
            };

            /** Toggle between chart and table view */
            toggleTableView?: {
                show?: boolean;
            };

            /** Toggle between line/bar chart type */
            toggleSeriesType?: {
                show?: boolean;
                callback?: (echartsInstance: ECharts) => void;
            };

            /** Toggle between non-stacked, stacked and 100% stacked series */
            toggleStackingMode?: {
                show?: boolean;
            };
        };
    };
    /** If true, then chart series will be included in common legend */
    includeInCommonLegend?: boolean;
    /** HTML string for chart documentation */
    chartDocumentation?: string;
}

export interface EChartsDashboardItemFactoryOptions
    extends IDashboardItemFactoryOptions {
    createdInstanceId: string;
    componentClass: 'EchartsDashboardItemComponent';
}

@Component({
    selector: 'app-echarts-dashboard-item',
    templateUrl: './echarts-dashboard-item.component.html',
    styleUrls: ['./echarts-dashboard-item.component.scss'],
})
export class EchartsDashboardItemComponent implements OnInit {
    @Input()
    dashboardItemFactory!: IDashboardItemFactory;
    dashboardItem!: IEChartsDashboardItem;

    @Input()
    dataLoaderEmitter!: EventEmitter<IDataLoaderResult>;

    /** Property representing latest data loader result */
    dataLoaderResult: IDataLoaderResult = DataLoaderResult.getLoading(
        [],
        QueryContextService.EMPTY
    );

    @Input()
    chartHeight: EChartsDashboardItemHeight = '200';

    // Properties related to fullscreen mode
    isFullscreen = false;
    fullscreenBox?: {
        top: number;
        left: number;
        width: number;
        height: number;
    };
    resizeObserver?: ResizeObserver;
    get fullscreenContainer(): HTMLElement | null {
        return this.dashboard.fullscreenContainerClassName;
    }
    @HostBinding('style.display') private hostDisplay = '';

    chartInstance: ECharts | undefined;
    copySQLDisabled = true;
    echartsInitOpts = {};
    ngChartClass =
        "{'echarts-container-800': chartHeight == '800', 'echarts-container-600': chartHeight == '600', 'echarts-container-400': chartHeight == '400', 'echarts-container': true}";

    echartsOption: EChartsOption = {
        echartsOption: { legend: {}, xAxis: { type: 'value' }, yAxis: {} },
    };
    oldEchartsOption: EChartsOption = {
        echartsOption: { legend: {}, xAxis: { type: 'value' }, yAxis: {} },
    };

    includeInCommonLegend!: boolean;

    // error handling
    showErrorIcon: boolean = false;
    showErrorOverlay: boolean = false;
    errorTitle: string = '';
    errorMessage: string = '';

    // properties related to chart/table view
    private _chartType: 'chart' | 'table' = 'chart';
    /** Indicates if echarts view is active and chart is visible. Used in ngIf */
    isActiveEchartsView$ = new BehaviorSubject<boolean>(true);
    /** Indicates if table view is active and table is visible. Used in ngIf  */
    isActiveTableView$ = new BehaviorSubject<boolean>(false);

    /**
     * A BehaviorSubject that holds the title for the menu item used to toggle the chart type.
     * Initially set to 'Toggle table view'.
     */
    menuItemToggleChartTypeTitle$ = new BehaviorSubject<string>(
        'Toggle table view'
    );
    menuItemToggleChartTypeIcon: string = 'table';

    menuItemToggleSeriesTypeTitle$ = new BehaviorSubject<string>(
        'Switch to bar chart'
    );
    menuItemToggleSeriesTypeIcon: string = 'line-chart';

    pivotData$ = new BehaviorSubject<any[][]>([]);

    /**
     * Persistent options for EChartsDashboardItem
     */
    itemPersistentOptions!: IEChartsDashboardItemPersistentOptions;

    private dataLoaderSubscriber = (dataLoaderResult: IDataLoaderResult) => {
        // deep clone dataLoaderResult
        const startTime = performance.now();
        this.dataLoaderResult = JSON.parse(JSON.stringify(dataLoaderResult));
        const endTime = performance.now();
        this.logger.debug(
            `DataLoaderResult processing time: ${endTime - startTime} ms.`
        );
        // console.log({
        //     msg: `[EChartsDashboardItem][${this.dashboardItem.instanceId}] Got dataLoaderResult`,
        //     dataLoaderResult,
        // });

        // apply only if echarts was already assigned
        // otherwise results will be applied when it will be assigned
        if (this.chartInstance) {
            this.applyDataLoaderResult(dataLoaderResult);
        }
    };

    private itemPersistentOptionsSubscriber = (
        dashboardContext: IDashboardContext | null
    ) => {
        const itemPersistentOptions = dashboardContext?.dashboardItems?.[
            this.dashboardItemFactory.createdInstanceId
        ] ?? {
            itemId: this.dashboardItemFactory.createdInstanceId,
        };
        this.itemPersistentOptions = itemPersistentOptions;

        if (this.itemPersistentOptions.chartType) {
            this.toggleChartType(this.itemPersistentOptions.chartType);
        }

        if (this.itemPersistentOptions['fullscreenMode'] === true) {
            this.activateFullscreen();
        } else {
            this.deactivateFullscreen();
        }

        if (this.itemPersistentOptions.seriesType) {
            // this.echartsOption = changeSeriesType(
            //     this.itemPersistentOptions.seriesType,
            //     this.echartsOption
            // );

            this.setMenuItemToggleSeriesTypeTitle(
                this.itemPersistentOptions.seriesType === 'line'
                    ? 'bar'
                    : 'line'
            );
            this.seriesType = this.itemPersistentOptions.seriesType;
        }
        if (this.itemPersistentOptions.stackingMode) {
            this.stackingMode = this.itemPersistentOptions.stackingMode;
            // this.onStackedValueChange(this.stackingMode);
        }
    };

    applyDataLoaderResult(dataLoaderResult: IDataLoaderResult) {
        if (dataLoaderResult.isLoading) {
            this.chartInstance!.showLoading();
            this.legendService.clearChartSeries(this.dashboardItem.instanceId);
        } else {
            this.chartInstance!.hideLoading();
            // if status not ok or loading
            if (dataLoaderResult.status != 'ok') {
                // if errorType = 'unsupportedColumn' then show error message
                // otherwise show statusMessage
                if (dataLoaderResult.status == 'unsupported-column') {
                    this.showOverlayMessage(
                        'Unsupported filter',
                        dataLoaderResult.statusMessage
                    );
                } else {
                    this.showErrorIcon = true;
                    this.showOverlayMessage(
                        'Error',
                        dataLoaderResult.statusMessage
                    );
                }
            } else {
                this.showErrorIcon = false;
            }
        }
        if (dataLoaderResult.sqlAndJsProcessing != SQL_NOT_AVAILABLE) {
            // Temporarily disable until functionality will work
            // this.copySQLDisabled = false;
        }

        if (dataLoaderResult.status == 'ok') {
            this.hideOverlayMessage();

            // Process new data and create new echartsOption using composeData function provided by dashboardItem
            this.echartsOption = this.dashboardItem.composeData(
                dataLoaderResult.data,
                dataLoaderResult.queryContext,
                this.chartInstance!,
                XYChartDashoardItemFactory.effectiveOptions(
                    this.dashboardItem.initFactoryOptions,
                    this.itemPersistentOptions
                )
            );

            // update echartsOption if some properties of itemPersistentOptions are set
            if (this.itemPersistentOptions.chartType) {
                this._chartType = this.itemPersistentOptions.chartType;
            }
            if (this.itemPersistentOptions.seriesType) {
                this.echartsOption = changeSeriesType(
                    this.itemPersistentOptions.seriesType,
                    this.echartsOption
                );
            }

            // if echarts is active view
            if (this.isActiveEchartsView$.value) {
                this.chartInstance!.setOption(this.echartsOption, true);

                if (this.includeInCommonLegend) {
                    let isSplitApplied =
                        dataLoaderResult.queryContext.datasourceColumns.some(
                            (column) => column.state.isSplitApplied
                        );
                    if (!isSplitApplied) {
                        // if split is not applied, register empty legend
                        this.legendService.registerChartSeries(
                            this.dashboardItem.instanceId,
                            [{ splitName: '', splitId: '', color: '' }]
                        );
                    } else {
                        // get series from option
                        let series = this.echartsOption.series as any[];
                        // if split is applied, register legend
                        this.legendService.registerChartSeries(
                            this.dashboardItem.instanceId,
                            series.map((s) => {
                                // get series color
                                const color = (
                                    this.chartInstance! as any
                                ).getVisual({ seriesId: s.id }, 'color');
                                return {
                                    splitName: s.name,
                                    splitId: s.datasetId ?? s.name,
                                    color: color ?? 'green',
                                };
                            })
                        );
                    }
                }
                this.setMenuItemToggleSeriesTypeTitle(
                    getSeriesType(this.echartsOption) === 'line'
                        ? 'bar'
                        : 'line'
                );
            }

            if (this.isActiveTableView$.value) {
                this.pivotData$.next(
                    echartsExportDataSplitGroup(this.echartsOption)
                );
            }
        }
    }

    constructor(
        private legendService: CommonLegendService,
        public notification: NzNotificationService,
        private modal: NzModalService,
        private dashboard: DashboardService,
        private logger: LoggerService
    ) {}

    ngOnInit(): void {
        this.dashboardItem =
            this.dashboardItemFactory.create() as IEChartsDashboardItem;
        this.itemPersistentOptions = {
            itemId: this.dashboardItem.instanceId,
        };

        this.dashboard
            .isDataLoadingAllowed$(this.dashboardItem.instanceId)
            .pipe(
                tap((allowed) => {
                    if (!allowed) this.onFullscreenSuspended();
                }),
                switchMap((allowed) =>
                    allowed ? this.dataLoaderEmitter : NEVER
                )
            )
            .subscribe(this.dataLoaderSubscriber);

        this.dashboard.currentDashboardContext$.subscribe(
            this.itemPersistentOptionsSubscriber
        );

        this.dashboard
            .isDataLoadingAllowed$(this.dashboardItem.instanceId)
            .subscribe((allowed) => {
                this.hostDisplay = allowed ? '' : 'none';
            });

        // default seriesType and stackingMode
        this.seriesType =
            this.dashboardItem.initFactoryOptions.echartsSeriesType ?? 'line';
        this.stackingMode =
            this.dashboardItem.initFactoryOptions.stackingMode ?? 'none';

        this.includeInCommonLegend =
            this.dashboardItem.includeInCommonLegend ?? true;
        // console.log({msg: '[EchartsDashboardItemComponent.ngOnInit]', dashboardItem: this.dashboardItem});

        if (this.includeInCommonLegend) {
            this.legendService.toggleLegend$.subscribe((seriesId) => {
                // if error do nothing
                if (this.isError()) {
                    return;
                }
                //get series from option
                let series = this.echartsOption.series as any[];
                //find series with id
                // if series is undefined
                if (!series) {
                    console.log('series is undefined');
                }
                let seriesToToggle = series.filter(
                    (s) => s.datasetId == seriesId
                );
                //toggle visibility
                if (seriesToToggle.length > 0) {
                    seriesToToggle.forEach((s) => {
                        this.chartInstance?.dispatchAction({
                            type: 'legendToggleSelect',
                            name: s.id,
                        });
                    });
                }
            });

            this.legendService.registeredLegendItems$
                .pipe(debounceTime(50))
                .subscribe(() => {
                    if (!this.isFullscreen) return;
                    this.resizeFullscreen();
                });
        }
    }

    /**
     * Suspends other charts when full-screen mode is enabled.
     *
     * Hides loading, detaches chart listeners, shows an overlay, and clears legend.
     */
    private onFullscreenSuspended(): void {
        this.chartInstance?.hideLoading();
        this.legendService.clearChartSeries(this.dashboardItem.instanceId);
    }

    public onChartInit(e: ECharts) {
        this.chartInstance = e;

        // apply last data loader results
        // initial class result is defined as Loading
        this.applyDataLoaderResult(this.dataLoaderResult);
    }

    public copySQLToCliboard() {
        if (this.dataLoaderResult) {
            // this.clipboard.copy(this.dataLoaderResult.sqlAndJsProcessing);
            // this._snackBar.open('Source SQL copied to clipboard', 'OK', {
            //     duration: 3000,
            // });
        } else {
            // this._snackBar.open('Error. Missing data to copy.', 'OK', {
            //     duration: 3000,
            // });
        }
    }

    public copyStatusMessage() {
        if (this.dataLoaderResult) {
            // console.log(`${this.dataLoaderResult.statusMessage}`);
            // this.clipboard.copy(this.dataLoaderResult.statusMessage);
            // this._snackBar.open('statusMessage copied to clipboard', 'OK', {
            //     duration: 3000,
            // });
        } else {
            // this._snackBar.open('Error. Missing data to copy.', 'OK', {
            //     duration: 3000,
            // });
        }
    }

    /**
     * Resizes fullscreen layout.
     *
     * Triggers a chart resize if `fullscreenContainer` exists.
     */
    private resizeFullscreen(): void {
        if (this.fullscreenContainer) {
            this.updateFullscreenBox();
            this.chartInstance?.resize();
        }
    }

    /**
     * Window resize handler.
     *
     * When in fullscreen, recalculates layout and resizes the chart.
     */
    @HostListener('window:resize')
    public onWindowResize() {
        if (!this.isFullscreen) return;
        this.resizeFullscreen();
    }

    /**
     * Activates fullscreen mode.
     *
     * Starts observing `fullscreenContainer` and syncs sizes.
     */
    public activateFullscreen(): void {
        if (this.isFullscreen) return;

        this.isFullscreen = true;

        if (this.fullscreenContainer) {
            if (!this.resizeObserver) {
                this.resizeObserver = new ResizeObserver(() =>
                    this.resizeFullscreen()
                );
            }
            this.resizeObserver.observe(this.fullscreenContainer);
        }

        this.dashboard.setActiveFullscreen(
            this.dashboardItemFactory.createdInstanceId
        );
        this.resizeFullscreen();

        this.updateFullscreenPersistentOption();
    }

    /**
     * Deactivates fullscreen mode.
     *
     * Stops observing `fullscreenContainer`, resizes the chart, and updates state.
     */
    public deactivateFullscreen(): void {
        if (!this.isFullscreen) return;

        this.isFullscreen = false;

        if (this.fullscreenContainer && this.resizeObserver) {
            this.resizeObserver.unobserve(this.fullscreenContainer);
        }
        this.resizeObserver?.disconnect();
        this.resizeObserver = undefined;

        this.dashboard.clearActiveFullscreen(
            this.dashboardItemFactory.createdInstanceId
        );
        this.chartInstance?.resize();

        this.updateFullscreenPersistentOption();
    }

    /**
     * Updates the current fullscreen flag for this item.
     */
    private updateFullscreenPersistentOption(): void {
        const id = this.dashboardItemFactory.createdInstanceId;

        this.itemPersistentOptions = {
            ...this.itemPersistentOptions,
            fullscreenMode: this.isFullscreen,
        };

        this.dashboard.setDashboardItemPersistentOptions(
            { [id]: this.itemPersistentOptions },
            false
        );
    }

    /**
     * Updates the fullscreen box.
     *
     * Reads `fullscreenContainer` bounds and stores them in `fullscreenBox`.
     */
    public updateFullscreenBox = (): void => {
        if (!this.fullscreenContainer) return;
        const dimensions = this.fullscreenContainer.getBoundingClientRect();
        this.fullscreenBox = {
            top: dimensions.top,
            left: dimensions.left,
            width: dimensions.width,
            height: dimensions.height,
        };
    };

    public canShowCardTitle() {
        if (typeof this.dashboardItem.title === 'function') {
            return !!this.dashboardItem.title(
                this.dataLoaderResult.queryContext
            );
        }
        return !!this.dashboardItem.title;
    }

    public getCardTitle() {
        if (typeof this.dashboardItem.title === 'function') {
            return this.dashboardItem.title(this.dataLoaderResult.queryContext);
        }
        return this.dashboardItem.title;
    }

    public canShowCardSubTitle() {
        return !!this.dashboardItem.subtitle;
    }

    public canShowExportDataToCSV() {
        if (
            !!this.dashboardItem.toolbox &&
            !!this.dashboardItem.toolbox.feature &&
            !this.dashboardItem.toolbox.feature?.exportDataToCSV
        ) {
            return false;
        }

        return true;
    }

    public canShowExportDataToXLSX() {
        if (
            !!this.dashboardItem.toolbox &&
            !!this.dashboardItem.toolbox.feature &&
            !this.dashboardItem.toolbox.feature?.exportDataToXLSX
        ) {
            return false;
        }

        return true;
    }

    /**
     * Exports the currently visible data to CSV.
     */
    public exportVisibleDataToCSV() {
        // Get the current dashboard item options
        let itemOptions = this.dashboardItem;

        // Format the chart title for use in the filename
        const chartTitle =
            this.getCardTitle()?.toLowerCase().replace(/ /g, '_') ??
            'chart_data';

        // Format the current date and time for use in the filename
        const formattedDate = format(new Date(), 'yyyyMMdd_HHmmss');

        // Construct the filename using the dashboard name, chart title, and current date/time
        const filename = `${itemOptions.dashboardName}_${chartTitle}_${formattedDate}`;

        // Create the data exporter
        const exporter = (() => {
            // Default data exporter creator
            const defaultCreator = () =>
                dataExporterCreator({
                    type: 'csv',
                    filename,
                    dataExporter: echartsExportDataSplitGroup,
                });

            // Use the custom data exporter if it exists, otherwise use the default
            return (
                itemOptions.toolbox?.feature?.exportDataToCSV?.dataExporter ??
                defaultCreator()
            );
        })();

        // Export the data
        exporter(this.echartsOption);
    }

    /**
     * Exports the currently visible data to Excel.
     */
    public exportVisibleDataToXLSX() {
        // Get the current dashboard item options
        const itemOptions = this.dashboardItem;

        // Format the chart title for use in the filename
        const chartTitle =
            this.getCardTitle()?.toLowerCase().replace(/ /g, '_') ??
            'chart_data';

        // Format the current date and time for use in the filename
        const formattedDate = format(new Date(), 'yyyyMMdd_HHmmss');

        // Construct the filename using the dashboard name, chart title, and current date/time
        const filename = `${itemOptions.dashboardName}_${chartTitle}_${formattedDate}`;

        // Create the data exporter
        const exporter = (() => {
            // Default data exporter creator
            const defaultCreator = () =>
                dataExporterCreator({
                    type: 'xlsx',
                    filename,
                    dataExporter: echartsExportDataSplitGroup,
                });

            // Use the custom data exporter if it exists, otherwise use the default
            return (
                itemOptions.toolbox?.feature?.exportDataToXLSX?.dataExporter ??
                defaultCreator()
            );
        })();

        // Export the data
        exporter(this.echartsOption);
    }
    /**
     * Exports the currently visible data to Clipboard.
     */
    public exportVisibleDataToClipboard() {
        // Get the current dashboard item options
        const itemOptions = this.dashboardItem;

        // Create the data exporter
        const exporter = (() => {
            // Default data exporter creator
            const defaultCreator = () =>
                dataExporterCreator({
                    type: 'clipboard',
                    filename: '',
                    dataExporter: echartsExportDataSplitGroup,
                });

            // Use the custom data exporter if it exists, otherwise use the default
            return (
                itemOptions.toolbox?.feature?.exportDataToClipboard
                    ?.dataExporter ?? defaultCreator()
            );
        })();

        // Export the data
        exporter(this.echartsOption);

        this.notification.blank(
            'Copy to clipboard',
            'Data copied to clipboard.',
            {
                nzDuration: 3000,
                nzPlacement: 'bottom',
            }
        );
    }

    public dataToTableView() {
        const pivotData = echartsExportDataSplitGroup(this.echartsOption);

        return pivotData;
    }

    public canShowToggleSeriesType() {
        if (
            !!this.dashboardItem.toolbox &&
            !!this.dashboardItem.toolbox.feature &&
            !this.dashboardItem.toolbox.feature?.toggleSeriesType
        ) {
            return false;
        }

        return true;
    }

    seriesType!: 'line' | 'bar';
    /**
     * Toggles between line/bar chart type
     */
    public toggleSeriesType() {
        const seriesType = getSeriesType(this.echartsOption);
        const nextSeriesType = seriesType === 'line' ? 'bar' : 'line';

        this.onSeriesTypeChange(nextSeriesType);
    }

    /**
     * Handles the change of the series type for the chart.
     *
     * @param seriesType - The new series type for the chart, either 'line' or 'bar'.
     *
     * Updates the persistent options for the dashboard item with the new series type,
     * composes the new chart options based on the updated series type, and applies
     * the new options to the chart instance. Also updates the menu item title for
     * toggling the series type.
     */
    public onSeriesTypeChange(seriesType: 'line' | 'bar') {
        this.itemPersistentOptions.seriesType = seriesType;
        this.dashboard.setDashboardItemPersistentOptions(
            {
                [this.dashboardItemFactory.createdInstanceId]:
                    this.itemPersistentOptions,
            },
            // new options are already processed in this component, so there is no need to reapply them
            false
        );

        this.echartsOption = this.dashboardItem.composeData(
            this.dataLoaderResult.data,
            this.dataLoaderResult.queryContext,
            this.chartInstance!,
            XYChartDashoardItemFactory.effectiveOptions(
                this.dashboardItem.initFactoryOptions,
                this.itemPersistentOptions
            )
        );

        // TODO remove this
        this.echartsOption = changeSeriesType(seriesType, this.echartsOption);
        this.chartInstance?.setOption(this.echartsOption, true);

        this.setMenuItemToggleSeriesTypeTitle(seriesType);
    }

    setMenuItemToggleSeriesTypeTitle(type: 'line' | 'bar') {
        this.menuItemToggleSeriesTypeTitle$.next(`Switch to ${type} chart`);
        this.menuItemToggleSeriesTypeIcon = `${type}-chart`;
    }

    stackingMode!: StackingMode;

    public canShowToggleStackingMode() {
        if (
            !!this.dashboardItem.toolbox &&
            !!this.dashboardItem.toolbox.feature &&
            !this.dashboardItem.toolbox.feature?.toggleStackingMode
        ) {
            return false;
        }

        return true;
    }

    public onStackedValueChange(stackedValue: StackingMode) {
        this.itemPersistentOptions.stackingMode = stackedValue;
        this.dashboard.setDashboardItemPersistentOptions(
            {
                [this.dashboardItemFactory.createdInstanceId]:
                    this.itemPersistentOptions,
            },
            // new options are already processed in this component, so there is no need to reapply them
            false
        );

        this.echartsOption = this.dashboardItem.composeData(
            this.dataLoaderResult.data,
            this.dataLoaderResult.queryContext,
            this.chartInstance!,
            XYChartDashoardItemFactory.effectiveOptions(
                this.dashboardItem.initFactoryOptions,
                this.itemPersistentOptions
            )
        );

        // const newEchartsOption = applyStackingMode(
        //     stackedValue,
        //     this.echartsOption,
        //     this.chartInstance!,
        //     this.dashboardItem.initFactoryOptions,
        //     this.dataLoaderResult.queryContext
        // );
        this.chartInstance?.setOption(this.echartsOption, true);
    }

    showOverlayMessage(title: string, message: string) {
        this.errorTitle = title;
        this.errorMessage = message;
        this.showErrorOverlay = true;
    }

    hideOverlayMessage() {
        this.showErrorOverlay = false;
    }

    isError() {
        return this.showErrorOverlay;
    }

    public canShowToggleChartType() {
        if (
            !!this.dashboardItem.toolbox &&
            !!this.dashboardItem.toolbox.feature &&
            !this.dashboardItem.toolbox.feature?.toggleTableView
        ) {
            return false;
        }

        return true;
    }

    /**
     * Toggles between chart and table view
     * @param targetChartType - optional parameter to force chart type
     */
    public toggleChartType(targetChartType?: 'chart' | 'table') {
        // if current chart type is the same as target chart type, then return
        if (targetChartType && targetChartType === this._chartType) {
            return;
        }

        let emitActiveChartType = (newType: 'chart' | 'table') => {
            this.isActiveEchartsView$.next(newType === 'chart');
            this.isActiveTableView$.next(newType === 'table');
        };

        let setToggleTableViewTitle = (type: 'chart' | 'table') => {
            this.menuItemToggleChartTypeTitle$.next(`Toggle ${type} view`);
            this.menuItemToggleChartTypeIcon =
                type === 'chart' ? 'line-chart' : 'table';
        };

        const nextChartType = this._chartType === 'chart' ? 'table' : 'chart';

        if (nextChartType === 'table') {
            this.pivotData$.next(
                echartsExportDataSplitGroup(this.echartsOption)
            );
        }
        setToggleTableViewTitle(this._chartType);
        emitActiveChartType(nextChartType);
        this._chartType = nextChartType;
        this.itemPersistentOptions.chartType = nextChartType;
        this.dashboard.setDashboardItemPersistentOptions(
            {
                [this.dashboardItemFactory.createdInstanceId]:
                    this.itemPersistentOptions,
            },
            // new options are already processed in this component, so there is no need to reapply them
            false
        );
    }

    public changeDatasetTo2dArray() {
        // console.log('changeDatasetFormat');
        const options = this.echartsOption;
        this.oldEchartsOption = { ...options };
        // if dataset is not array, then return
        if (!Array.isArray(options.dataset)) {
            return;
        }

        // measure time of execution
        const startTime1 = performance.now();
        const dataset = options.dataset.map(changeDatasetSourceFormat);
        const endTime1 = performance.now();
        console.log({
            msg: 'changeDatasetFormat',
            time: endTime1 - startTime1,
        });
        options.dataset = dataset;

        this.chartInstance!.showLoading();

        // measure time of execution is seconds
        const startTime = performance.now();
        this.chartInstance?.setOption(options, true);
        const endTime = performance.now();
        console.log({
            msg: 'changeDatasetFormat.setOption',
            time: endTime - startTime,
        });
        this.chartInstance!.hideLoading();
    }

    public restoreOriginalDataset() {
        this.echartsOption = { ...this.oldEchartsOption };

        this.chartInstance!.showLoading();
        // measure time of execution
        const startTime = performance.now();
        this.chartInstance?.setOption(this.oldEchartsOption, true);
        const endTime = performance.now();
        console.log({
            msg: 'restoreOriginalDataset.setOption',
            time: endTime - startTime,
        });
        this.chartInstance!.hideLoading();
    }

    public copyOption() {
        navigator.clipboard.writeText(JSONPrettyStringify(this.echartsOption));
    }

    public showLoading() {
        this.chartInstance!.showLoading();
    }

    public isLocalhost() {
        return isDevMode();
    }

    public showChartDocumentation() {
        const title: string = this.getCardTitle();
        DashboardService.showChartDocumentation(
            this.modal,
            title,
            this.dashboardItem.chartDocumentation
        );
    }
}
